import { useCallback } from 'react';
import { rage, useObservable } from '~/rage';
import {
	HandlingMode,
	IModalReplyDefault,
	ModalButtonColor,
	ModalType,
} from '~/shared';
import {
	setActiveStage,
	setModeValue,
	setUnsavedChanges,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../redux';

const useModal = () => {
	const dispatch = useLayoutDispatch();
	const saveAbsoluteIndex = useLayoutSelector(
		(state) => state.handlingList.saveAbsoluteIndex,
	);
	const presetList = useLayoutSelector(
		(state) => state.handlingList.savePresetList,
	);
	const unsavedChanges = useLayoutSelector(
		(state) => state.handling.unsavedChanges,
	);
	const modalState = useObservable(rage.modal.getCurrentModal());
	const modalVisible = useLayoutSelector(
		(state) => state.handling.modalVisible,
	);
	const initialHandling = useLayoutSelector(
		(state) => state.handling.initialHandling,
	);

	const handleSetupNewPresetModal = useCallback(() => {
		if (modalState || modalVisible) {
			return;
		}

		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: 'Вы действительно хотите сбросить свои настройки и установить новые?',
				acceptButton: { text: 'Установить', color: ModalButtonColor.Primary },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			({ isAccepted }: IModalReplyDefault) => {
				if (isAccepted) {
					rage.handlingTuning.applyHandling();
					dispatch(setUnsavedChanges(false));
				}
			},
		);
	}, [modalState, modalVisible]);

	const handleSetupSaveModal = useCallback(() => {
		if (modalState || modalVisible) {
			return;
		}

		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: `Вы действительно хотите сбросить свои настройки и установить “${presetList[saveAbsoluteIndex].name}”?`,
				acceptButton: { text: 'Установить', color: ModalButtonColor.Primary },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			({ isAccepted }: IModalReplyDefault) => {
				if (isAccepted) {
					rage.handlingTuning.applyPreset(saveAbsoluteIndex);
					dispatch(setUnsavedChanges(false));
				}
			},
		);
	}, [saveAbsoluteIndex, presetList, modalState, modalVisible]);

	const handleDeleteModal = useCallback(() => {
		if (modalState || modalVisible) {
			return;
		}

		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: `Вы действительно хотите удалить настройку "${presetList?.[saveAbsoluteIndex]?.name}"?`,
				acceptButton: { text: 'Удалить', color: ModalButtonColor.Danger },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			({ isAccepted }: IModalReplyDefault) => {
				if (isAccepted) {
					rage.handlingTuning.removePresetByIndex(saveAbsoluteIndex);
				}
			},
		);
	}, [presetList, saveAbsoluteIndex, modalState, modalVisible]);

	const handleResetModal = useCallback(
		(name: string, resetId: string) => {
			if (modalState || modalVisible) {
				return;
			}

			rage.modal.showModal(
				{
					type: ModalType.Default,
					title: 'Подтверждение',
					text: `Вы действительно хотите сбросить настройку "${name}"?`,
					acceptButton: { text: 'Сбросить', color: ModalButtonColor.Danger },
					cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
				},
				({ isAccepted }: IModalReplyDefault) => {
					if (isAccepted) {
						rage.handlingTuning.resetPropertyHandling(resetId);
					}
				},
			);
		},
		[modalState, modalVisible],
	);

	const handleResetAllModal = useCallback(() => {
		if (modalState || modalVisible) {
			return;
		}

		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: 'Вы действительно хотите сбросить все настройки?',
				acceptButton: { text: 'Сбросить', color: ModalButtonColor.Danger },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			({ isAccepted }: IModalReplyDefault) => {
				if (isAccepted) {
					rage.handlingTuning.resetAllSectionHandling().then(async () => {
						await rage.handlingTuning.applyHandling();
						dispatch(setUnsavedChanges(false));
					});
				}
			},
		);
	}, [modalState, modalVisible]);

	const handleUnsavedChangesLeaveModal = useCallback(
		(onLeave: () => void) => {
			if (modalState || modalVisible) {
				return;
			}

			if (!unsavedChanges) {
				onLeave();
				return;
			}

			rage.modal.showModal(
				{
					type: ModalType.Default,
					title: 'Подтверждение',
					text: 'У вас имеются несохраненные настройки, вы действительно хотите выйти?',
					acceptButton: { text: 'Выйти', color: ModalButtonColor.Danger },
					cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
				},
				({ isAccepted }: IModalReplyDefault) => {
					if (isAccepted) {
						if (initialHandling && Object.keys(initialHandling).length > 0) {
							rage.handlingTuning.setHandling(initialHandling);
						}
						dispatch(setUnsavedChanges(false));
						onLeave();
					}
				},
			);
		},
		[unsavedChanges, modalState, modalVisible, initialHandling],
	);

	const handleUnsavedChangesExitModal = useCallback(() => {
		handleUnsavedChangesLeaveModal(() =>
			dispatch(setActiveStage(StageTypes.Category)),
		);
	}, [handleUnsavedChangesLeaveModal]);

	const handleUnsavedChangesModeModal = useCallback(
		(mode: HandlingMode) => {
			if (!unsavedChanges) {
				rage.handlingTuning.setHandlingMode(mode);
				dispatch(setModeValue(mode));
				return;
			}

			rage.modal.showModal(
				{
					type: ModalType.Default,
					title: 'Подтверждение',
					text: 'У вас имеются несохраненные настройки, вы действительно хотите сменить режим?',
					acceptButton: { text: 'Сменить', color: ModalButtonColor.Danger },
					cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
				},
				({ isAccepted }: IModalReplyDefault) => {
					if (isAccepted) {
						if (initialHandling && Object.keys(initialHandling).length > 0) {
							rage.handlingTuning.setHandling(initialHandling);
						}
						dispatch(setUnsavedChanges(false));
						rage.handlingTuning.setHandlingMode(mode);
						dispatch(setModeValue(mode));
					}
				},
			);
		},
		[unsavedChanges, initialHandling],
	);

	return {
		handleSetupNewPresetModal,
		handleSetupSaveModal,
		handleDeleteModal,
		handleResetModal,
		handleResetAllModal,
		handleUnsavedChangesExitModal,
		handleUnsavedChangesLeaveModal,
		handleUnsavedChangesModeModal,
	};
};

export { useModal };
